<template>
  <div class="receipt-printer-wrapper">
    <!-- Encabezado -->
    <div class="page-header mb-6">
      <div class="page-header-icon">
        <v-icon color="white" size="28">mdi-printer-pos</v-icon>
      </div>
      <div>
        <h2 class="page-title">Impresión de Recibos</h2>
        <p class="page-subtitle">Busca una transacción para generar y descargar la colilla de pago</p>
      </div>
    </div>

    <!-- Buscador -->
    <v-card class="search-card elevation-3 mb-6">
      <v-card-text class="pa-8">
        <v-row align="center">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="idTransaccion"
              label="ID de Transacción"
              placeholder="Ingresa el ID de la transacción"
              outlined
              prepend-inner-icon="mdi-magnify"
              hide-details
              @keyup.enter="buscarTransaccion"
              color="#294697"
              class="custom-field"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn
              block
              x-large
              color="#294697"
              dark
              class="search-btn"
              :loading="isLoading"
              @click="buscarTransaccion"
            >
              <v-icon left>mdi-magnify</v-icon>
              BUSCAR
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Diálogo de Impresión -->
    <v-dialog v-model="dialogPrint" max-width="400" persistent>
      <v-card class="receipt-dialog">
        <v-card-title class="receipt-header white--text pa-4">
          <v-icon left color="white">mdi-file-document-outline</v-icon>
          Vista Previa de Colilla
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogPrint = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <div id="printable-receipt" class="receipt-content">
            <div class="receipt-logo text-center mb-4">
              <v-img :src="logoUrl" contain max-height="40"></v-img>
            </div>

            <div class="receipt-divider mb-4"></div>

            <div class="receipt-info">
              <div class="info-row">
                <span class="label">Fecha / Hora:</span>
                <span class="value">{{ currentDateTime }}</span>
              </div>
              <div class="info-row">
                <span class="label">Vendedor:</span>
                <span class="value">{{ userName }}</span>
              </div>
              <div class="info-row">
                <span class="label">Cédula:</span>
                <span class="value">{{ userCedula }}</span>
              </div>

              <div class="receipt-divider my-4"></div>

              <div class="info-row highlight">
                <span class="label">ID Transacción:</span>
                <span class="value">{{ transaccionData.id_transaccion }}</span>
              </div>

              <template v-if="transaccionData.es_multiple">
                <div
                  v-for="(valor, index) in transaccionData.valores"
                  :key="'valor-' + index"
                  class="info-row"
                >
                  <span class="label">VALOR {{ index + 1 }}:</span>
                  <span class="value">{{ formatCurrency(valor) }}</span>
                </div>
                <div class="info-row total mt-4">
                  <span class="label">VALOR TOTAL:</span>
                  <span class="value">{{ formatCurrency(transaccionData.valor_total) }}</span>
                </div>
              </template>
              <div v-else class="info-row total mt-4">
                <span class="label">VALOR PAGADO:</span>
                <span class="value">{{ formatCurrency(transaccionData.valor_total) }}</span>
              </div>
            </div>

            <div class="receipt-divider my-4"></div>

            <div class="receipt-footer text-center mt-6">
              <p>¡Gracias por su transacción!</p>
              <div class="barcode-placeholder">
                <v-icon size="60">mdi-barcode</v-icon>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 bg-light">
          <v-btn text color="red" @click="dialogPrint = false">CANCELAR</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="#294697" dark class="px-6" @click="printReceipt()">
            <v-icon left>mdi-printer</v-icon>
            IMPRIMIR
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import env from "../../env";
import Swal from "sweetalert2";

export default {
  name: "ReceiptPrinter",
  data() {
    return {
      idTransaccion: "",
      isLoading: false,
      dialogPrint: false,
      transaccionData: {
        id_transaccion: "",
        valor_total: 0,
        es_multiple: false,
        valores: []
      },
      currentDateTime: "",
      userName: localStorage.getItem("nombre") || "N/A",
      userCedula: localStorage.getItem("cedula") || "N/A",
      logoUrl: require("../../../assets/nova/NOMBRESOLO.png")
    };
  },
  methods: {
    async buscarTransaccion() {
      if (!this.idTransaccion.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Atención",
          text: "Por favor ingresa un ID de transacción",
          confirmButtonColor: "#294697"
        });
        return;
      }

      this.isLoading = true;
      try {
        const idUsuario = localStorage.getItem("id");
        const idEmpresa = localStorage.getItem("idEmpresa");
        const params = new URLSearchParams({
          id_transaccion: this.idTransaccion.trim(),
          id_empresa: idEmpresa != null ? String(idEmpresa) : "",
          id_usuario: idUsuario != null ? String(idUsuario) : "",
        });
        const response = await axios.get(
          `${env.endpoint}premios/consultar_transaccion?${params.toString()}`
        );
        const data = response.data;

        if (!data || !data.status) {
          Swal.fire({
            icon: "error",
            title: "No encontrado",
            text: (data && data.mensaje) || "No se encontró la transacción solicitada",
            confirmButtonColor: "#294697"
          });
          return;
        }

        this.updateCurrentDateTime();

        if (data.tipo === "multiples" && Array.isArray(data.registros) && data.registros.length > 0) {
          this.transaccionData = this.construirTransaccionMultiples(data.registros);
          this.dialogPrint = true;
          return;
        }

        if (data.tipo === "oficina_principal") {
          Swal.fire({
            icon: "info",
            title: "Oficina principal",
            text: data.mensaje,
            confirmButtonColor: "#294697"
          });
          return;
        }

        if (data.tipo === "pagado") {
          Swal.fire({
            icon: "info",
            title: "Premio pagado",
            text: data.mensaje || "Este premio ya fue pagado.",
            confirmButtonColor: "#294697"
          });
          return;
        }

        if (data.tipo === "disponible" && data.datos) {
          this.transaccionData = this.mapearRegistro(data.datos);
          this.dialogPrint = true;
          return;
        }

        Swal.fire({
          icon: "error",
          title: "No encontrado",
          text: data.mensaje || "No se encontró la transacción solicitada",
          confirmButtonColor: "#294697"
        });
      } catch (error) {
        console.error("Error al consultar transacción:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Ocurrió un problema al buscar la transacción",
          confirmButtonColor: "#294697"
        });
      } finally {
        this.isLoading = false;
      }
    },
    campoRegistro(registro, fragmento) {
      if (!registro || typeof registro !== "object") return "";
      const clave = Object.keys(registro).find((k) =>
        k.toLowerCase().includes(fragmento.toLowerCase())
      );
      const valor = clave ? registro[clave] : "";
      return valor != null && valor !== "" ? valor : "";
    },
    mapearRegistro(registro) {
      const valorTotal = this.campoRegistro(registro, "valor total") || this.campoRegistro(registro, "valor premio");
      return {
        id_transaccion:
          this.campoRegistro(registro, "id transacc") || this.idTransaccion,
        valor_total: Number(valorTotal) || 0,
        es_multiple: false,
        valores: []
      };
    },
    construirTransaccionMultiples(registros) {
      const valores = registros.map((r) => {
        const valorTotal = this.campoRegistro(r, "valor total") || this.campoRegistro(r, "valor premio");
        return Number(valorTotal) || 0;
      });
      const valor_total = valores.reduce((suma, v) => suma + v, 0);
      const primero = registros[0];

      return {
        id_transaccion:
          this.campoRegistro(primero, "id transacc") || this.idTransaccion,
        valor_total,
        es_multiple: true,
        valores
      };
    },
    updateCurrentDateTime() {
      const now = new Date();
      this.currentDateTime = now.toLocaleString();
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0
      }).format(value);
    },
    async registrarImpresionAuditoria() {
      try {
        const idUsuario = localStorage.getItem("id");
        const idEmpresa = localStorage.getItem("idEmpresa");
        const payload = new URLSearchParams();
        payload.append("id_transaccion", this.transaccionData.id_transaccion || this.idTransaccion.trim());
        payload.append("id_usuario", idUsuario != null ? String(idUsuario) : "");
        payload.append("id_empresa", idEmpresa != null ? String(idEmpresa) : "");
        payload.append("valor_premio", String(this.transaccionData.valor_total || 0));

        await axios.post(`${env.endpoint}premios/registrar_impresion`, payload);
      } catch (err) {
        console.warn("No se pudo registrar la auditoría de impresión:", err);
      }
    },
    printReceipt(elementId = "printable-receipt") {
      this.registrarImpresionAuditoria();

      const el = document.getElementById(elementId);
      if (!el) return;
      const content = el.innerHTML;
      const printWindow = window.open("", "_blank", "height=600,width=400");

      printWindow.document.write("<html><head><title>Imprimir Colilla</title>");
      printWindow.document.write("<style>");
      printWindow.document.write(`
        @page { margin: 0; }
        body {
          font-family: 'Courier New', Courier, monospace;
          padding: 8mm 5mm;
          margin: 0;
          width: 80mm;
          color: #000;
        }
        .receipt-content { width: 100%; display: flex; flex-direction: column; align-items: center; }
        .receipt-logo { text-align: center; margin-bottom: 4mm; width: 100%; }
        .receipt-logo img { width: 50mm; filter: grayscale(1); margin: 0 auto; display: block; }
        .receipt-divider { border-top: 1px dashed #000; height: 1px; width: 100%; margin: 3mm 0; }
        .receipt-info { width: 100%; }
        .info-row {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          margin-bottom: 3mm;
          font-size: 11pt;
          line-height: 1.1;
        }
        .label { font-weight: bold; margin-bottom: 0.5mm; }
        .value { width: 100%; }
        .total {
          font-size: 15pt;
          font-weight: bold;
          margin-top: 5mm;
          border-top: 1px solid #000;
          padding-top: 3mm;
          width: 100%;
        }
        .text-center { text-align: center; width: 100%; }
        .mt-6 { margin-top: 8mm; }
        .v-icon, .v-image, .barcode-placeholder, .receipt-divider:last-of-type { display: none !important; }
        .receipt-logo img { display: block !important; }
      `);
      printWindow.document.write("</style></head><body>");
      printWindow.document.write('<div class="receipt-content">');

      printWindow.document.write(content);
      printWindow.document.write("</div></body></html>");

      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap');

.receipt-printer-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-header-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #294697, #1D3C7F);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(41, 70, 151, 0.35);
}

.page-title {
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.page-subtitle {
  font-size: 0.95rem;
  color: #64748b;
  margin: 0;
}

.search-card {
  border-radius: 16px !important;
  border: 1px solid rgba(0,0,0,0.05);
}

.custom-field >>> .v-input__slot {
  background: #f8fafc !important;
}

.search-btn {
  border-radius: 12px !important;
  font-weight: 600;
  letter-spacing: 1px;
}

.receipt-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.receipt-header {
  background: linear-gradient(135deg, #294697, #1D3C7F);
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
}

.receipt-content {
  font-family: 'Courier New', Courier, monospace;
  color: #334155;
}

.receipt-divider {
  border-top: 1px dashed #cbd5e1;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.label {
  font-weight: 600;
  color: #64748b;
}

.value {
  font-weight: 600;
  color: #1e293b;
}

.highlight .value {
  color: #294697;
}

.total {
  border-top: 2px solid #1e293b;
  padding-top: 12px;
}

.total .label {
  font-size: 1.1rem;
  color: #1e293b;
}

.total .value {
  font-size: 1.3rem;
  color: #294697;
}

.receipt-footer {
  font-size: 0.9rem;
}

.bg-light {
  background-color: #f8fafc;
}

.barcode-placeholder {
  opacity: 0.3;
}
</style>
